import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Alert,
  ActivityIndicator,
  Pressable,
  Image,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors, useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { AuthAPI } from '@/services/residents/api/AuthAPI';
import { useAdminLoginMutation } from '@/store/api';
import { useResponsive } from '@/hooks/common/useResponsive';
import { ResioLogo } from '@/assets/ResioLogo';
import { AuthHeader } from '@/components/auth/AuthHeader';
import { useAppSelector } from '@/store';
import { SVG_COMPONENTS } from '@/assets/admin';
import { AuthStackParamList } from '@/navigation/residents/mobile/AuthNavigator';

type ForgotPasswordRouteProp = RouteProp<AuthStackParamList, 'ForgotPassword'>;

export const ForgotPasswordScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<ForgotPasswordRouteProp>();
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const { colors, semantic } = useThemeColors();
  const styles = useThemedStyles(makeStyles);

  // Get admin mode from auth state
  const { user } = useAppSelector((state) => state.auth);
  const isAdminMode = user?.roles && user.roles.length > 0;
  
  const [email, setEmail] = useState(route.params?.email || '');
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    setFormError(null);
    
    if (!validateEmail(email)) {
      setFormError('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);
    try {
      const targetPath = '/reset-password';
      // Use appropriate API based on mode
      // Admin forgot password uses v2 users/verification with templateName like Admin API did
      const response = await AuthAPI.sendVerification(email, targetPath);
      
      if (response.success) {
        // Navigate to AccountVerification screen (matching web apps flow)
        navigation.navigate('AccountVerification', { 
          email, 
          isSignup: false // This is forgot password flow
        });
      } else {
        setFormError(response.message || 'Failed to send reset email.');
      }
    } catch (error: any) {
      setFormError(error.message || 'An error occurred while sending the reset email.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigation.goBack();
  };

  // Dynamic styles based on responsive breakpoints and admin mode
  const dynamicStyles = {
    pageWrapper: [
      styles.pageWrapper,
      isAdminMode ? styles.pageWrapperAdmin : styles.pageWrapperResident,
    ],
    splitContainer: [
      styles.splitContainer,
      isMobile && styles.splitContainerMobile,
      isTablet && styles.splitContainerTablet,
      isDesktop && styles.splitContainerDesktop,
    ],
    imagePane: [
      styles.imagePane,
      isMobile && styles.imagePaneMobile,
      isTablet && styles.imagePaneTablet,
      isDesktop && styles.imagePaneDesktop,
    ],
    formPane: [
      styles.formPane,
      isAdminMode ? styles.formPaneAdmin : styles.formPaneResident,
      isMobile && styles.formPaneMobile,
      isTablet && styles.formPaneTablet,
      isDesktop && styles.formPaneDesktop,
    ],
    formInner: [
      styles.formInner,
      isMobile && styles.formInnerMobile,
      isTablet && styles.formInnerTablet,
      isDesktop && styles.formInnerDesktop,
    ],
    title: [
      styles.title,
      isAdminMode ? styles.titleAdmin : styles.titleResident,
      isMobile && styles.titleMobile,
      isTablet && styles.titleTablet,
      isDesktop && styles.titleDesktop,
    ],
    link: [
      styles.link,
      isAdminMode ? styles.linkAdmin : styles.linkResident,
    ],
  };


  return (
    <View style={dynamicStyles.pageWrapper}>
      <AuthHeader show={true} />

      <View style={dynamicStyles.splitContainer}>
        {/* Left side - Image pane (hidden on mobile, visible on desktop) */}
        <View style={dynamicStyles.imagePane}>
          <Image
            source={isAdminMode ? require('@/assets/images/login.png') : require('@/assets/images/sign-up.png')}
            style={styles.image}
            resizeMode="cover"
          />
        </View>
        
        {/* Right side - Form pane */}
        <View style={dynamicStyles.formPane}>
          <View style={dynamicStyles.formInner}>
            <Text style={dynamicStyles.title}>Forgotten Password?</Text>
            <Text style={styles.description}>
              If you have forgotten your password, enter the email address associated with your account.
              We'll send you a verification message to reset your password.
            </Text>

            <View style={styles.formContent}>
              {/* Email Input */}
              <View style={styles.inputContainer}>
                <Text style={styles.label}>Email</Text>
                <View style={styles.emailInputContainer}>
                  <View style={styles.emailIcon}>
                    <SVG_COMPONENTS.EMAIL_SOLID
                      width={20}
                      height={20}
                      fill={semantic.icon.muted}
                    />
                  </View>
                  <TextInput
                    style={[styles.input, styles.emailInput]}
                    value={email}
                    onChangeText={setEmail}
                    placeholder="name@domain.com"
                    placeholderTextColor={colors.text.primary}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                    textContentType="emailAddress"
                    autoComplete="email"
                    editable={!isLoading}
                    autoFocus
                  />
                </View>
              </View>

              {/* Error Message */}
              {!!formError && (
                <View style={styles.errorBox}>
                  <Text style={styles.errorText}>{formError}</Text>
                </View>
              )}

              {/* Button Container - Different layouts for admin vs resident */}
              {isAdminMode ? (
                // Admin mode: Back and Next buttons side by side (matching admin-web)
                <View style={styles.buttonContainer}>
                  <TouchableOpacity
                    style={[styles.backButton]}
                    onPress={handleBackToLogin}
                    disabled={isLoading}
                  >
                    <Text style={styles.backButtonText}>Back</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.submitButton, (!email.trim() || isLoading) && styles.submitButtonDisabled]}
                    onPress={handleSubmit}
                    disabled={isLoading || !email.trim()}
                  >
                    {isLoading ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.submitButtonText}>Next</Text>
                    )}
                  </TouchableOpacity>
                </View>
              ) : (
                // Resident mode: Only Next button, full width (matching resio-web)
                <TouchableOpacity
                  style={[styles.submitButtonFullWidth, (!email.trim() || isLoading) && styles.submitButtonDisabled]}
                  onPress={handleSubmit}
                  disabled={isLoading || !email.trim()}
                >
                  {isLoading ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.submitButtonText}>Next</Text>
                  )}
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

export const ForgotPasswordDesktopContent: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<ForgotPasswordRouteProp>();
  const { colors, semantic } = useThemeColors();
  const styles = useThemedStyles(makeStyles);

  const { user } = useAppSelector((state) => state.auth);
  const isAdminMode = user?.roles && user.roles.length > 0;

  const [email, setEmail] = useState(route.params?.email || '');
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const validateEmail = (emailVal: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailVal);
  };

  const handleSubmit = async () => {
    setFormError(null);
    if (!validateEmail(email)) {
      setFormError('Please enter a valid email address.');
      return;
    }
    setIsLoading(true);
    try {
      const targetPath = '/reset-password';
      const response = await AuthAPI.sendVerification(email, targetPath);
      if (response.success) {
        navigation.navigate('AccountVerification', { email, isSignup: false });
      } else {
        setFormError(response.message || 'Failed to send reset email.');
      }
    } catch (error: any) {
      setFormError(error.message || 'An error occurred while sending the reset email.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigation.goBack();
  };

  const titleStyle = [styles.title, styles.titleDesktop, isAdminMode ? styles.titleAdmin : styles.titleResident];

  return (
    <View style={desktopContentStyles.formInner}>
      <Text style={titleStyle}>Forgotten Password?</Text>
      <Text style={styles.description}>
        If you have forgotten your password, enter the email address associated with your account.
        We'll send you a verification message to reset your password.
      </Text>

      <View style={styles.formContent}>
        <View style={styles.inputContainer}>
          <Text style={styles.label}>Email</Text>
          <View style={styles.emailInputContainer}>
            <View style={styles.emailIcon}>
              <SVG_COMPONENTS.EMAIL_SOLID width={20} height={20} fill={semantic.icon.muted} />
            </View>
            <TextInput
              style={[styles.input, styles.emailInput]}
              value={email}
              onChangeText={setEmail}
              placeholder="name@domain.com"
              placeholderTextColor={colors.text.primary}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              textContentType="emailAddress"
              autoComplete="email"
              editable={!isLoading}
              autoFocus
            />
          </View>
        </View>

        {!!formError && (
          <View style={styles.errorBox}>
            <Text style={styles.errorText}>{formError}</Text>
          </View>
        )}

        {isAdminMode ? (
          <View style={styles.buttonContainer}>
            <TouchableOpacity style={styles.backButton} onPress={handleBackToLogin} disabled={isLoading}>
              <Text style={styles.backButtonText}>Back</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.submitButton, (!email.trim() || isLoading) && styles.submitButtonDisabled]}
              onPress={handleSubmit}
              disabled={isLoading || !email.trim()}
            >
              {isLoading ? <ActivityIndicator color="#FFFFFF" /> : <Text style={styles.submitButtonText}>Next</Text>}
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity
            style={[styles.submitButtonFullWidth, (!email.trim() || isLoading) && styles.submitButtonDisabled]}
            onPress={handleSubmit}
            disabled={isLoading || !email.trim()}
          >
            {isLoading ? <ActivityIndicator color="#FFFFFF" /> : <Text style={styles.submitButtonText}>Next</Text>}
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const desktopContentStyles = StyleSheet.create({
  formInner: {
    width: '100%',
    maxWidth: 512,
    paddingHorizontal: 32,
    paddingTop: 40,
    paddingBottom: 40,
    minHeight: '100%' as any,
    display: 'flex' as any,
    flexDirection: 'column' as any,
    justifyContent: 'center' as any,
  },
});

const makeStyles = ({ colors, semantic }: ThemeContextValue) => StyleSheet.create({
  // Base styles
  pageWrapper: {
    flex: 1,
    ...(Platform.OS === 'web' && { minHeight: '100vh' as any }),
  },
  pageWrapperAdmin: {
    backgroundColor: colors.bg.base,
  },
  pageWrapperResident: {
    backgroundColor: colors.bg.base,
  },

  // Header styles
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: semantic.border.secondary,
    backgroundColor: 'transparent',
    ...Platform.select({
      web: {
        position: 'sticky' as any,
        top: 0,
        zIndex: 10,
      },
    }),
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoIcon: {
    marginRight: 8,
    width: 64,
    height: 64,
  },
  logoText: {
    fontSize: Platform.select({ web: 48, default: 32 }),
    fontWeight: '600',
    color: colors.text.primary,
    letterSpacing: 0.5,
  },

  // Layout styles
  splitContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderColor: semantic.border.secondary,
    borderBottomWidth: Platform.select({ web: 1, default: 0 }),
    ...(Platform.OS === 'web' && { maxWidth: 1200 }),
    alignSelf: 'center',
    width: '100%',
  },
  splitContainerMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    maxWidth: '100%',
  },
  splitContainerTablet: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 768,
  },
  splitContainerDesktop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 1200,
  },

  imagePane: {
    ...Platform.select({
      web: {
        display: 'flex' as any,
        width: 384,
        height: 'calc(100vh - 97px)' as any,
        flexShrink: 0,
        overflow: 'hidden',
        position: 'relative' as any,
      },
      default: {
        display: 'none',
      },
    }),
  },
  imagePaneMobile: {
    display: 'none',
  },
  imagePaneTablet: {
    display: 'none',
  },
  imagePaneDesktop: {
    display: 'flex' as any,
    width: 384,
    height: 'calc(100vh - 97px)' as any,
    flexShrink: 0,
    overflow: 'hidden',
    position: 'relative' as any,
  },
  image: {
    width: '100%',
    height: '100%',
    ...(Platform.OS === 'web' && {
      objectFit: 'cover' as any,
      position: 'absolute' as any,
      top: 0,
      left: 0,
    }),
  },

  formPane: {
    width: 512,
    alignItems: 'center',
    justifyContent: Platform.select({ web: 'flex-start', default: 'flex-start' }),
    borderColor: semantic.border.secondary,
    ...Platform.select({
      web: {
        borderLeftWidth: 1,
        borderRightWidth: 1,
        height: 'calc(100vh - 97px)' as any,
        flexShrink: 0,
        overflow: 'auto' as any,
      },
      default: {
        flex: 1,
        borderLeftWidth: 1,
        borderRightWidth: 1,
      },
    }),
  },
  formPaneAdmin: {
    backgroundColor: colors.bg.base,
  },
  formPaneResident: {
    backgroundColor: colors.bg.base,
  },
  formPaneMobile: {
    width: '100%',
    borderLeftWidth: 0,
    borderRightWidth: 0,
    height: 'auto' as any,
    justifyContent: 'flex-start',
    paddingHorizontal: 16,
  },
  formPaneTablet: {
    width: '100%',
    maxWidth: 512,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    height: 'auto' as any,
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  formPaneDesktop: {
    width: 512,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    height: 'calc(100vh - 97px)' as any,
    justifyContent: 'flex-start',
    flexShrink: 0,
    overflow: 'auto' as any,
  },

  formInner: {
    width: '100%',
    maxWidth: 512,
    paddingHorizontal: 32,
    paddingVertical: Platform.select({ web: 40, default: 24 }),
    ...Platform.select({
      web: {
        paddingTop: 40,
        paddingBottom: 40,
        minHeight: '100%' as any,
        display: 'flex' as any,
        flexDirection: 'column' as any,
        justifyContent: 'center' as any,
      },
    }),
  },
  formInnerMobile: {
    paddingHorizontal: 16,
    paddingVertical: 24,
    maxWidth: '100%',
  },
  formInnerTablet: {
    paddingHorizontal: 24,
    paddingVertical: 32,
    maxWidth: 480,
  },
  formInnerDesktop: {
    paddingHorizontal: 32,
    paddingVertical: 40,
    maxWidth: 512,
    minHeight: '100%' as any,
    display: 'flex' as any,
    flexDirection: 'column' as any,
    justifyContent: 'center' as any,
  },

  // Content styles
  title: {
    fontSize: Platform.select({ web: 30, default: 24 }),
    fontWeight: '600',
    textAlign: Platform.select({ web: 'left', default: 'center' }),
    marginBottom: 24,
    marginTop: 24,
  },
  titleAdmin: {
    color: colors.text.primary,
  },
  titleResident: {
    color: colors.text.primary,
  },
  titleMobile: {
    fontSize: 24,
    textAlign: 'center',
    marginBottom: 20,
  },
  titleTablet: {
    fontSize: 24,
    textAlign: 'center',
    marginBottom: 20,
  },
  titleDesktop: {
    fontSize: 30,
    textAlign: 'left',
    marginBottom: 24,
  },

  description: {
    fontSize: 14,
    color: colors.text.secondary,
    lineHeight: 20,
    marginBottom: 32,
    textAlign: Platform.select({ web: 'left', default: 'center' }),
  },

  formContent: {
    gap: 24,
  },

  // Input styles
  inputContainer: {
    marginBottom: 0,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text.secondary,
    marginBottom: 8,
  },
  emailInputContainer: {
    position: 'relative',
    width: '100%',
  },
  emailIcon: {
    position: 'absolute',
    left: 12,
    top: 12,
    zIndex: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  input: {
    height: 48,
    borderWidth: 1,
    borderColor: semantic.border.input,
    borderRadius: 6,
    paddingHorizontal: 16,
    fontSize: 16,
    color: colors.text.primary,
    backgroundColor: semantic.background.input,
    width: '100%',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
    }),
  },
  emailInput: {
    paddingLeft: 40,
  },

  // Error styles
  errorBox: {
    marginTop: 0,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: colors.card.bg,
    borderColor: semantic.status.error,
    borderWidth: 1,
    borderRadius: 8,
  },
  errorText: {
    color: semantic.status.error,
    fontSize: 13,
  },

  // Button styles
  buttonContainer: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 8,
  },
  backButton: {
    flex: 1,
    height: 48,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: semantic.border.secondary,
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backButtonText: {
    color: colors.text.secondary,
    fontSize: 16,
    fontWeight: '600',
  },
  submitButton: {
    flex: 1,
    height: 48,
    backgroundColor: PRIMITIVE_COLORS.resioPink,
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  submitButtonFullWidth: {
    width: '100%',
    height: 48,
    backgroundColor: PRIMITIVE_COLORS.resioPink,
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
  },
  submitButtonDisabled: {
    backgroundColor: '#9CA3AF',
    opacity: 1,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },

  // Link styles
  link: {
    textDecorationLine: 'underline',
    fontSize: 14,
    fontWeight: '500',
  },
  linkAdmin: {
    color: '#f55560', // intentional brand pink link (same in light & dark)
  },
  linkResident: {
    color: '#f55560', // intentional brand pink link (same in light & dark)
  },
});
